import { supabase } from './supabaseClient';

/**
 * Featured images for watches.
 *
 * Uploads are downscaled in the browser with a canvas before they leave the
 * machine. That is what keeps this feature free of any server: a modern phone
 * photo is 3-8 MB, and resizing it here means no Node process, no `sharp`, and
 * no Edge Function -- the same job the news pipeline needs a build step for.
 *
 * The bucket is private (see 0003_watch_images.sql), so `watches.image_url`
 * holds a storage PATH and reading one means signing it first.
 */

const BUCKET = 'watch-images';

/** Long edge, in px. Large enough to look sharp on a retina detail page. */
const MAX_EDGE = 1400;
const JPEG_QUALITY = 0.85;

/** Refuse absurd input before decoding it -- a 100 MB file would hang the tab. */
const MAX_INPUT_BYTES = 25 * 1024 * 1024;

const SIGNED_URL_TTL_SECONDS = 60 * 60;

export const imagePath = (userId: string, watchId: string) =>
  `${userId}/${watchId}.jpg`;

/**
 * Decodes a file and re-encodes it as a bounded JPEG.
 *
 * `imageOrientation: 'from-image'` matters: photos taken in portrait carry
 * their rotation in EXIF, and drawing the raw bitmap without honouring it
 * lands them sideways.
 */
async function downscale(file: File): Promise<Blob> {
  // 'from-image' is older than this TypeScript lib's ImageOrientation union,
  // which still only knows 'none' | 'flipY' -- hence the cast rather than a
  // change of behaviour.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const options = { imageOrientation: 'from-image' } as any;
  const bitmap = await createImageBitmap(file, options);

  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not process that image in this browser.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>(resolve =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
  );
  if (!blob) throw new Error('Could not process that image.');
  return blob;
}

export const watchImages = {
  /**
   * Uploads a photo for a watch and returns the storage path to save on the
   * row. Upserts, so replacing a photo overwrites rather than accumulating
   * orphans -- the path is derived from the watch id, not the file name.
   */
  upload: async (watchId: string, file: File): Promise<string> => {
    if (!file.type.startsWith('image/')) {
      throw new Error('That file is not an image.');
    }
    if (file.size > MAX_INPUT_BYTES) {
      throw new Error('That image is too large. Pick one under 25 MB.');
    }

    const { data: auth } = await supabase.auth.getUser();
    const userId = auth.user?.id;
    if (!userId) throw new Error('You are not signed in.');

    const path = imagePath(userId, watchId);
    const blob = await downscale(file);

    const { error } = await supabase.storage
      .from(BUCKET)
      .upload(path, blob, { contentType: 'image/jpeg', upsert: true });
    if (error) throw error;

    return path;
  },

  /**
   * A temporary readable URL for a stored path. Returns null rather than
   * throwing: a missing or unsignable image should leave the page working,
   * not replace it with an error.
   */
  signedUrl: async (path: string): Promise<string | null> => {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
    if (error) return null;
    return data?.signedUrl ?? null;
  },

  /**
   * Signs many paths in one request, for the list and tile views.
   *
   * Batched deliberately: signing per row would fire one request per watch on
   * every visit to the list. Missing or unsignable paths are simply absent
   * from the result, so a broken image hides rather than breaking the page.
   */
  signedUrls: async (paths: string[]): Promise<Record<string, string>> => {
    const unique = paths.filter((p, i) => p && paths.indexOf(p) === i);
    if (unique.length === 0) return {};

    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrls(unique, SIGNED_URL_TTL_SECONDS);
    if (error || !data) return {};

    const out: Record<string, string> = {};
    data.forEach(entry => {
      if (entry.path && entry.signedUrl) out[entry.path] = entry.signedUrl;
    });
    return out;
  },

  /** Deletes a stored photo. Safe to call for a path that is already gone. */
  remove: async (path: string): Promise<void> => {
    const { error } = await supabase.storage.from(BUCKET).remove([path]);
    if (error) throw error;
  },
};
